/**
 * ImageUploader — reusable image slot control used by /landing-settings.
 * Renders a preview thumbnail, upload/replace/delete buttons, alt-text
 * input, object-position picker (9-point grid) and object-fit dropdown.
 *
 * Talks to /api/landing-settings/admin/images/{slot}. The parent component
 * only needs to pass the slot id and the current image metadata read from
 * the CMS draft.
 */
import React, { useRef, useState } from "react";
import { Upload, Trash2, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const OBJECT_FITS = [
  { value: "cover", label: "Cover (fill & crop)" },
  { value: "contain", label: "Contain (fit inside)" },
];

// 3x3 grid of object-position anchors — the admin picks a focal point so
// responsive cropping never hides key subjects.
const POSITIONS = [
  ["0% 0%",    "50% 0%",   "100% 0%"],
  ["0% 50%",   "50% 50%",  "100% 50%"],
  ["0% 100%",  "50% 100%", "100% 100%"],
];

export default function ImageUploader({
  slot,
  meta,          // { asset_id, alt, object_position, object_fit }
  onUpdated,     // called with the updated slot object after every change
  recommended,   // { width, height, ratio, max_kb } — shown as guidance
  label,
  description,
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const previewUrl = meta?.asset_id ? assetUrl(meta.asset_id) : null;

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Image too large — max 3 MB before compression.");
      return;
    }
    const okTypes = ["image/png", "image/jpeg", "image/webp", "image/jpg"];
    if (file.type && !okTypes.includes(file.type)) {
      toast.error("Please pick a WebP, PNG or JPG file.");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/landing-settings/admin/images/${slot}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdated({ ...(meta || {}), asset_id: data.asset_id });
      toast.success("Image uploaded (draft)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!meta?.asset_id) return;
    if (!confirm("Remove this image from the draft?")) return;
    setBusy(true);
    try {
      await api.delete(`/landing-settings/admin/images/${slot}`);
      onUpdated({ ...(meta || {}), asset_id: null });
      toast.success("Image removed (draft)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const setMeta = async (patch) => {
    const next = { ...(meta || {}), ...patch };
    onUpdated(next);
    try {
      await api.put(`/landing-settings/admin/images/${slot}/meta`, patch);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update image metadata");
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4" data-testid={`image-slot-${slot}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          {label && <div className="text-sm font-bold text-slate-800">{label}</div>}
          {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
          {recommended && (
            <div className="text-[11px] text-slate-500 mt-1">
              Recommended: {recommended.width}×{recommended.height}px
              {recommended.ratio ? ` · ratio ${recommended.ratio}` : ""}
              {" · WebP / PNG / JPG · max 3 MB"}
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-[220px_1fr]">
        {/* Preview */}
        <div className="rounded-lg overflow-hidden bg-white border border-slate-200 flex items-center justify-center h-40 relative">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={meta?.alt || slot}
              className="w-full h-full"
              style={{
                objectFit: meta?.object_fit || "cover",
                objectPosition: meta?.object_position || "50% 50%",
              }}
              data-testid={`image-slot-preview-${slot}`}
            />
          ) : (
            <div className="text-slate-300 flex flex-col items-center gap-1 text-xs">
              <ImageIcon className="w-6 h-6" />
              <span>No image</span>
            </div>
          )}
          {busy && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-[#0F62FE]" />
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/webp,image/png,image/jpeg,image/jpg"
              onChange={(e) => upload(e.target.files?.[0])}
              className="hidden"
              data-testid={`image-slot-input-${slot}`}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-[#0F62FE] text-white text-xs font-semibold hover:bg-[#0050E6] disabled:opacity-50"
              data-testid={`image-slot-upload-${slot}`}
            >
              <Upload className="w-3.5 h-3.5" /> {previewUrl ? "Replace" : "Upload"}
            </button>
            {previewUrl && (
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
                data-testid={`image-slot-delete-${slot}`}
              >
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            )}
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
              Alt text (accessibility)
            </label>
            <input
              type="text"
              value={meta?.alt || ""}
              onChange={(e) => onUpdated({ ...(meta || {}), alt: e.target.value })}
              onBlur={(e) => setMeta({ alt: e.target.value })}
              placeholder="Describe the image for screen readers"
              maxLength={200}
              className="w-full h-9 rounded-md border border-slate-300 bg-white px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              data-testid={`image-slot-alt-${slot}`}
            />
          </div>

          <div className="grid grid-cols-[1fr_auto] gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
                Focal point
              </label>
              <div className="inline-grid grid-cols-3 gap-1 p-1 bg-white rounded-md border border-slate-200">
                {POSITIONS.flat().map((pos) => (
                  <button
                    key={pos}
                    type="button"
                    onClick={() => setMeta({ object_position: pos })}
                    className={`w-6 h-6 rounded ${meta?.object_position === pos ? "bg-[#0F62FE]" : "bg-slate-200 hover:bg-slate-300"}`}
                    aria-label={`Focal ${pos}`}
                    title={pos}
                    data-testid={`image-slot-pos-${slot}-${pos.replace(/[% ]/g, "")}`}
                  />
                ))}
              </div>
              <div className="text-[10px] text-slate-400 mt-1 font-mono">{meta?.object_position || "50% 50%"}</div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
                Fit
              </label>
              <select
                value={meta?.object_fit || "cover"}
                onChange={(e) => setMeta({ object_fit: e.target.value })}
                className="h-9 rounded-md border border-slate-300 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                data-testid={`image-slot-fit-${slot}`}
              >
                {OBJECT_FITS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Public URL to a landing asset (works for preview and published views).
 * We serve via the backend so images survive cluster restarts and CDN
 * caching honours our Cache-Control header.
 */
export function assetUrl(assetId) {
  if (!assetId) return null;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/landing-settings/asset/${assetId}`;
}
